/**
 * MyInterviewsPage.js — PeoplesDock
 *
 * FIXES IN THIS VERSION:
 *
 * 1. ViewFeedbackModal — NEW read-only component
 *    Shows the rating + comments the interviewer submitted for a completed interview.
 *
 * 2. Completed interviews table — updated:
 *    Status column: "Feedback submitted" text → "View Feedback" button
 *    (shows the modal with stars + comments)
 *    Requires GET /ats/my-interviews to return feedback_rating + feedback_comments
 *    (fixed in ats.routes.js via LEFT JOIN interview_feedback)
 *
 * All other code is 100% identical to your version.
 */
import React, { useState, useEffect } from 'react';
import {
    Card, Table, Button, Badge, Modal, Form,
    Row, Col, Spinner, Alert,
} from 'react-bootstrap';
import {
    Calendar, Star, CheckCircle, MessageSquare,
    User, Briefcase, Clock, Video, Phone, Building2,
    RefreshCw,
} from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';
import { useAuth } from '../../context/AuthContext';

// ── Helpers ───────────────────────────────────────────────────────────────
const fmtDate = (d) => d
    ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';
const fmtDateTime = (d) => d
    ? new Date(d).toLocaleString('en-IN', {
        day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    : '—';
const STAGE_COLOURS = {
    Applied: 'secondary', Screening: 'info', Shortlisted: 'primary',
    Interview: 'warning', Technical: 'warning', 'HR Round': 'warning',
    Offer: 'success', Hired: 'success', Rejected: 'danger',
};

// ── Star Rating ───────────────────────────────────────────────────────────
const StarRating = ({ value, onChange, readonly = false, size = 28 }) => (
    <div className="d-flex gap-1 align-items-center">
        {[1, 2, 3, 4, 5].map(star => (
            <button key={star} type="button"
                onClick={() => !readonly && onChange?.(star)}
                style={{
                    background: 'none', border: 'none', padding: 2, lineHeight: 1,
                    cursor: readonly ? 'default' : 'pointer',
                }}>
                <Star
                    size={size}
                    fill={star <= value ? '#F59E0B' : 'none'}
                    color={star <= value ? '#F59E0B' : '#D1D5DB'}
                    strokeWidth={1.5}
                />
            </button>
        ))}
        {value > 0 && (
            <span className="ms-1 small text-muted">
                {['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'][value]}
            </span>
        )}
    </div>
);

// ── Feedback Submit Modal — unchanged ─────────────────────────────────────
const FeedbackModal = ({ interview, onClose, onDone }) => {
    const [rating,    setRating]    = useState(0);
    const [comments,  setComments]  = useState('');
    const [saving,    setSaving]    = useState(false);
    const [ratingErr, setRatingErr] = useState('');
    if (!interview) return null;
    const submit = async () => {
        if (!rating) { setRatingErr('Please select a rating'); return; }
        setRatingErr('');
        setSaving(true);
        try {
            await axiosInstance.post(`/ats/interviews/${interview.id}/feedback`, {
                rating,
                comments: comments.trim() || null,
            });
            toast.success('Feedback submitted! Interview marked as Completed.');
            onDone();
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to submit feedback');
        } finally {
            setSaving(false);
        }
    };
    const modeIcon = interview.mode === 'Video Call'
        ? <Video size={14} className="me-1 text-primary" />
        : interview.mode === 'Phone'
            ? <Phone size={14} className="me-1 text-success" />
            : <Building2 size={14} className="me-1 text-warning" />;
    return (
        <Modal show onHide={onClose} centered size="md" backdrop="static">
            <Modal.Header closeButton className="border-0">
                <div>
                    <Modal.Title className="fw-bold fs-6">Submit Interview Feedback</Modal.Title>
                    <div className="text-muted small mt-1">Share your assessment of the candidate</div>
                </div>
            </Modal.Header>
            <Modal.Body>
                {/* Interview summary card */}
                <div className="p-3 rounded mb-4" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
                    <Row className="g-2" style={{ fontSize: 13 }}>
                        <Col xs={12} sm={6}>
                            <div className="d-flex align-items-center gap-2">
                                <User size={14} color="#6b7280" />
                                <span className="text-muted">Candidate:</span>
                                <span className="fw-semibold ms-1">{interview.candidate_name}</span>
                            </div>
                        </Col>
                        <Col xs={12} sm={6}>
                            <div className="d-flex align-items-center gap-2">
                                <Briefcase size={14} color="#6b7280" />
                                <span className="text-muted">Position:</span>
                                <span className="fw-semibold ms-1">{interview.job_title}</span>
                            </div>
                        </Col>
                        <Col xs={12} sm={6}>
                            <div className="d-flex align-items-center gap-2">
                                <Clock size={14} color="#6b7280" />
                                <span className="text-muted">Scheduled:</span>
                                <span className="fw-medium ms-1">{fmtDateTime(interview.scheduled_at)}</span>
                            </div>
                        </Col>
                        <Col xs={12} sm={6}>
                            <div className="d-flex align-items-center gap-2">
                                {modeIcon}
                                <span className="text-muted">Mode:</span>
                                <span className="fw-medium ms-1">{interview.mode}</span>
                            </div>
                        </Col>
                    </Row>
                </div>
                <Row className="g-3">
                    <Col xs={12}>
                        <Form.Label className="small fw-bold">
                            Overall Rating <span className="text-danger">*</span>
                        </Form.Label>
                        <StarRating
                            value={rating}
                            onChange={(v) => { setRating(v); setRatingErr(''); }}
                            size={32}
                        />
                        {ratingErr && (
                            <div className="text-danger mt-1" style={{ fontSize: 12 }}>{ratingErr}</div>
                        )}
                        <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                            1 = Poor &nbsp;·&nbsp; 2 = Fair &nbsp;·&nbsp; 3 = Good &nbsp;·&nbsp;
                            4 = Very Good &nbsp;·&nbsp; 5 = Excellent
                        </div>
                    </Col>
                    <Col xs={12}>
                        <Form.Label className="small fw-bold">
                            Detailed Comments
                            <span className="text-muted fw-normal ms-1">(optional but recommended)</span>
                        </Form.Label>
                        <Form.Control
                            as="textarea" rows={5}
                            placeholder={`Consider sharing feedback on:\n• Technical skills and knowledge\n• Communication and presentation\n• Problem-solving approach\n• Cultural fit\n• Strengths and areas for improvement`}
                            value={comments}
                            onChange={e => setComments(e.target.value)}
                            style={{ resize: 'vertical', fontSize: 13 }}
                        />
                    </Col>
                    <Col xs={12}>
                        <Alert variant="info" className="py-2 small mb-0">
                            <strong>Note:</strong> Submitting this feedback will mark the interview
                            as <strong>Completed</strong> in the system.
                        </Alert>
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0">
                <Button variant="light" onClick={onClose} disabled={saving}>Cancel</Button>
                <Button onClick={submit} disabled={saving}
                    style={{ background: 'linear-gradient(135deg,#4f46e5,#7c3aed)', border: 'none' }}>
                    {saving
                        ? <><Spinner size="sm" className="me-2" />Submitting…</>
                        : <><CheckCircle size={14} className="me-2" />Submit Feedback</>
                    }
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// VIEW FEEDBACK MODAL — NEW
// Read-only. Shows the rating + comments the interviewer submitted.
// Opened by clicking "View Feedback" in the Completed interviews table.
// Data comes from iv.feedback_rating + iv.feedback_comments (returned by
// GET /ats/my-interviews after the LEFT JOIN fix in ats.routes.js).
// ════════════════════════════════════════════════════════════════════════════
const ViewFeedbackModal = ({ interview, onClose }) => {
    if (!interview) return null;
    const rating   = parseInt(interview.feedback_rating)   || 0;
    const comments = interview.feedback_comments || '';
    return (
        <Modal show onHide={onClose} centered size="md">
            <Modal.Header closeButton className="border-0">
                <div>
                    <Modal.Title className="fw-bold fs-6">
                        <Star size={16} className="me-2 text-warning" fill="#F59E0B" />
                        My Submitted Feedback
                    </Modal.Title>
                    <div className="text-muted small mt-1">
                        <strong>{interview.candidate_name}</strong> — {interview.job_title}
                    </div>
                </div>
            </Modal.Header>
            <Modal.Body>
                {/* Interview summary */}
                <div className="p-3 rounded mb-4" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
                    <Row className="g-1" style={{ fontSize: 13 }}>
                        <Col xs={12} sm={6}>
                            <span className="text-muted">Candidate:</span>
                            <span className="fw-medium ms-1">{interview.candidate_name}</span>
                        </Col>
                        <Col xs={12} sm={6}>
                            <span className="text-muted">Date:</span>
                            <span className="fw-medium ms-1">{fmtDate(interview.scheduled_at)}</span>
                        </Col>
                        <Col xs={12} sm={6}>
                            <span className="text-muted">Position:</span>
                            <span className="fw-medium ms-1">{interview.job_title}</span>
                        </Col>
                        <Col xs={12} sm={6}>
                            <span className="text-muted">Mode:</span>
                            <span className="fw-medium ms-1">{interview.mode}</span>
                        </Col>
                    </Row>
                </div>

                {/* Rating (read-only) */}
                <div className="mb-4">
                    <div className="small fw-bold text-muted text-uppercase mb-2" style={{ letterSpacing: '0.05em' }}>
                        Your Rating
                    </div>
                    {rating > 0 ? (
                        <StarRating value={rating} readonly size={28} />
                    ) : (
                        <span className="text-muted small">No rating on record</span>
                    )}
                </div>

                {/* Comments (read-only) */}
                <div>
                    <div className="small fw-bold text-muted text-uppercase mb-2" style={{ letterSpacing: '0.05em' }}>
                        Your Comments
                    </div>
                    {comments ? (
                        <div style={{
                            background: '#f8fafc', border: '1px solid #e5e7eb',
                            borderRadius: 10, padding: '16px',
                            fontSize: 14, color: '#374151', lineHeight: 1.75,
                            whiteSpace: 'pre-wrap',
                        }}>
                            {comments}
                        </div>
                    ) : (
                        <span className="text-muted small">No comments provided</span>
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer className="border-0">
                <Button variant="light" onClick={onClose}>Close</Button>
            </Modal.Footer>
        </Modal>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ════════════════════════════════════════════════════════════════════════════
const MyInterviewsPage = () => {
    const { user } = useAuth();
    const [interviews,      setInterviews]      = useState([]);
    const [loading,         setLoading]         = useState(true);
    const [feedbackFor,     setFeedbackFor]     = useState(null);  // submit feedback
    const [viewFeedbackFor, setViewFeedbackFor] = useState(null);  // view submitted feedback

    const load = () => {
        setLoading(true);
        axiosInstance.get('/ats/my-interviews')
            .then(r => setInterviews(r.data.data || []))
            .catch(() => toast.error('Failed to load interviews'))
            .finally(() => setLoading(false));
    };
    useEffect(load, []);

    const scheduled = interviews.filter(iv => iv.status === 'Scheduled');
    const completed = interviews.filter(iv => iv.status === 'Completed');
    const cancelled = interviews.filter(iv => iv.status === 'Cancelled');

    const getModeIcon = (mode) => {
        if (mode === 'Video Call') return <Video size={14} className="me-1" />;
        if (mode === 'Phone')      return <Phone size={14} className="me-1" />;
        return <Building2 size={14} className="me-1" />;
    };

    return (
        <div className="p-2 p-md-4">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-start mb-4 flex-wrap gap-3">
                <div>
                    <h4 className="fw-bold mb-1">
                        <Calendar size={22} className="me-2 text-primary" />
                        My Interviews
                    </h4>
                    <p className="text-muted small mb-0">
                        Interviews assigned to you as interviewer
                    </p>
                </div>
                <Button size="sm" variant="outline-secondary" onClick={load}>
                    <RefreshCw size={13} className="me-1" />Refresh
                </Button>
            </div>

            {/* Summary pills */}
            {!loading && (
                <Row className="g-3 mb-4">
                    {[
                        { label: 'Scheduled', count: scheduled.length, color: '#4f46e5', bg: '#eef2ff' },
                        { label: 'Completed', count: completed.length, color: '#059669', bg: '#ecfdf5' },
                        { label: 'Cancelled', count: cancelled.length, color: '#dc2626', bg: '#fef2f2' },
                        { label: 'Total',     count: interviews.length,color: '#374151', bg: '#f8fafc' },
                    ].map(({ label, count, color, bg }) => (
                        <Col key={label} xs={6} md={3}>
                            <Card className="border-0 shadow-sm text-center p-3">
                                <div style={{ fontSize: 28, fontWeight: 800, color }}>{count}</div>
                                <div className="text-muted small">{label}</div>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}

            {loading ? (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="text-muted small mt-2">Loading your interviews…</p>
                </div>
            ) : interviews.length === 0 ? (
                <Card className="border-0 shadow-sm">
                    <Card.Body className="text-center py-5 text-muted">
                        <Calendar size={48} className="mb-3 opacity-30" />
                        <h6 className="fw-semibold mb-1">No interviews assigned</h6>
                        <p className="small mb-0">
                            When HR assigns you as an interviewer, your interviews will appear here.
                        </p>
                    </Card.Body>
                </Card>
            ) : (
                <>
                    {/* ── Scheduled — Pending Feedback ───────────────────── */}
                    {scheduled.length > 0 && (
                        <div className="mb-4">
                            <h6 className="fw-bold mb-3 d-flex align-items-center gap-2">
                                <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#4f46e5', display: 'inline-block' }} />
                                Scheduled — Pending Feedback
                                <Badge bg="primary" pill style={{ fontSize: 11 }}>{scheduled.length}</Badge>
                            </h6>
                            <Row className="g-3">
                                {scheduled.map(iv => (
                                    <Col key={iv.id} xs={12} md={6} lg={4}>
                                        <Card className="border-0 shadow-sm h-100"
                                            style={{ borderLeft: '4px solid #4f46e5', borderRadius: 12 }}>
                                            <Card.Body className="p-4">
                                                <div className="d-flex align-items-center gap-2 mb-3">
                                                    <div style={{
                                                        width: 40, height: 40, borderRadius: '50%',
                                                        background: 'linear-gradient(135deg,#4f46e5,#7c3aed)',
                                                        color: '#fff', fontWeight: 700, fontSize: 16,
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        flexShrink: 0,
                                                    }}>
                                                        {(iv.candidate_name?.[0] || '?').toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <div className="fw-bold" style={{ fontSize: 15 }}>{iv.candidate_name}</div>
                                                        <div className="text-muted small">{iv.job_title}</div>
                                                    </div>
                                                </div>
                                                <div className="mb-3" style={{ fontSize: 13, color: '#6b7280' }}>
                                                    <div className="d-flex align-items-center gap-1 mb-1">
                                                        <Clock size={13} /><span>{fmtDateTime(iv.scheduled_at)}</span>
                                                    </div>
                                                    <div className="d-flex align-items-center gap-1 mb-1">
                                                        {getModeIcon(iv.mode)}<span>{iv.mode}</span>
                                                    </div>
                                                    {iv.application_stage && (
                                                        <div className="mt-2">
                                                            <Badge bg={STAGE_COLOURS[iv.application_stage] || 'secondary'} style={{ fontSize: 10 }}>
                                                                {iv.application_stage} stage
                                                            </Badge>
                                                        </div>
                                                    )}
                                                </div>
                                                {iv.meeting_link && (
                                                    <a href={iv.meeting_link} target="_blank" rel="noopener noreferrer"
                                                        className="btn btn-sm btn-outline-primary w-100 mb-3"
                                                        style={{ fontSize: 12, borderRadius: 8 }}>
                                                        <Video size={13} className="me-1" />Join Meeting
                                                    </a>
                                                )}
                                                <Button variant="primary" size="sm" className="w-100"
                                                    style={{ borderRadius: 8 }}
                                                    onClick={() => setFeedbackFor(iv)}>
                                                    <MessageSquare size={13} className="me-1" />Give Feedback
                                                </Button>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                ))}
                            </Row>
                        </div>
                    )}

                    {/* ── Completed ─────────────────────────────────────── */}
                    {completed.length > 0 && (
                        <div className="mb-4">
                            <h6 className="fw-bold mb-3 d-flex align-items-center gap-2">
                                <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#059669', display: 'inline-block' }} />
                                Completed
                                <Badge bg="success" pill style={{ fontSize: 11 }}>{completed.length}</Badge>
                            </h6>
                            <Card className="border-0 shadow-sm">
                                <Table hover responsive className="mb-0 small align-middle">
                                    <thead className="bg-light">
                                        <tr className="text-muted text-uppercase" style={{ fontSize: 10 }}>
                                            <th className="ps-4">Candidate</th>
                                            <th>Job</th>
                                            <th>Date</th>
                                            <th>Mode</th>
                                            <th className="pe-4">Feedback</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {completed.map(iv => (
                                            <tr key={iv.id}>
                                                <td className="ps-4 fw-medium">{iv.candidate_name}</td>
                                                <td>{iv.job_title}</td>
                                                <td>{fmtDate(iv.scheduled_at)}</td>
                                                <td>
                                                    <span className="d-flex align-items-center gap-1">
                                                        {getModeIcon(iv.mode)}{iv.mode}
                                                    </span>
                                                </td>
                                                <td className="pe-4">
                                                    {/* View Feedback button if rating exists, otherwise "Done" */}
                                                    {iv.feedback_rating ? (
                                                        <Button size="sm" variant="outline-warning"
                                                            style={{ fontSize: 11, borderRadius: 8, whiteSpace: 'nowrap' }}
                                                            onClick={() => setViewFeedbackFor(iv)}>
                                                            <Star size={12} className="me-1" fill="#F59E0B" color="#F59E0B" />
                                                            View Feedback
                                                        </Button>
                                                    ) : (
                                                        <span style={{ fontSize: 12, color: '#059669' }}>
                                                            <CheckCircle size={13} className="me-1" />
                                                            Submitted
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </Card>
                        </div>
                    )}

                    {/* ── Cancelled ─────────────────────────────────────── */}
                    {cancelled.length > 0 && (
                        <div>
                            <h6 className="fw-bold mb-3 d-flex align-items-center gap-2 text-muted">
                                <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#dc2626', display: 'inline-block' }} />
                                Cancelled
                                <Badge bg="danger" pill style={{ fontSize: 11 }}>{cancelled.length}</Badge>
                            </h6>
                            <Card className="border-0 shadow-sm">
                                <Table hover responsive className="mb-0 small align-middle">
                                    <thead className="bg-light">
                                        <tr className="text-muted text-uppercase" style={{ fontSize: 10 }}>
                                            <th className="ps-4">Candidate</th>
                                            <th>Job</th>
                                            <th className="pe-4">Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cancelled.map(iv => (
                                            <tr key={iv.id} style={{ opacity: 0.6 }}>
                                                <td className="ps-4">{iv.candidate_name}</td>
                                                <td>{iv.job_title}</td>
                                                <td className="pe-4">{fmtDate(iv.scheduled_at)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </Card>
                        </div>
                    )}
                </>
            )}

            {/* Submit feedback modal */}
            {feedbackFor && (
                <FeedbackModal
                    interview={feedbackFor}
                    onClose={() => setFeedbackFor(null)}
                    onDone={load}
                />
            )}

            {/* View submitted feedback modal — NEW */}
            {viewFeedbackFor && (
                <ViewFeedbackModal
                    interview={viewFeedbackFor}
                    onClose={() => setViewFeedbackFor(null)}
                />
            )}
        </div>
    );
};

export default MyInterviewsPage;
